import { useState, useCallback, startTransition } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { setUser, resetUser } from "../../../app/userSlice";
import authService from "../services/authService";
import { User } from "../../../types/user";
import { RootState } from "../../../app/store";
import { isEmpty } from "../../../lib/utils";
import { set, STORAGE_KEYS } from "../../../lib/storage";

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profile = useSelector((state: RootState) => state.user.value);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(!isEmpty(profile));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      const auth = getAuth();
      await signOut(auth);
      dispatch(resetUser());
      authService.clearAuthToken();
      setIsUserLoggedIn(false);
      startTransition(() => navigate("/"));
    } catch (err) {
      console.error("Logout error:", err);
      setError("Failed to logout");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate]);

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      setError(null);

      try {
        if (authService.isDevMode()) {
          // Dev mode: skip actual login
          dispatch(setUser({ id: 123, email, name: email } as User));
          setIsUserLoggedIn(true);
          startTransition(() => navigate("/dashboard"));
          setIsLoading(false);
          return;
        }

        const response = await authService.login(email, password);
        const { account, jwt_token } = response;

        // Check role
        if (
          process.env.REACT_APP_ALLOWED_ROLE &&
          account.role !== process.env.REACT_APP_ALLOWED_ROLE
        ) {
          setError("Please login with appropriate account!");
          setIsLoading(false);
          return;
        }

        // Set auth data
        authService.setAuthToken(jwt_token);
        set(STORAGE_KEYS.USER_ID, String(account.id));

        // Update Redux
        const userData: User = {
          id: account.id,
          name: account.name || (account as any).displayName || "",
          email: account.email,
          photoURL: account.photoURL,
          username: account.username,
          type: account.type,
          role: account.role,
        };

        dispatch(setUser(userData));
        setIsUserLoggedIn(true);
        startTransition(() => navigate("/dashboard"));
      } catch (err: any) {
        const errorMsg =
          err?.message ||
          err?.toString() ||
          "Login failed. Please try again.";
        setError(errorMsg);
        console.error("Login error:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, navigate]
  );

  const loginWithGoogle = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const idToken = credential?.idToken;

      if (!idToken) {
        throw new Error("Failed to obtain Google ID token");
      }

      const { token, user } = await authService.googleLogin(idToken);

      authService.setAuthToken(token);

      const jwtPayload = JSON.parse(atob(token.split(".")[1]));

      dispatch(
        setUser({
          id: jwtPayload.id,
          name: result.user.displayName || user.username,
          email: user.email,
          username: user.username,
          role: user.role,
        } as User)
      );
      setIsUserLoggedIn(true);
      startTransition(() => navigate("/dashboard"));
    } catch (err: any) {
      const errorMsg =
        err?.message || "Google sign-in failed. Please try again.";
      setError(errorMsg);
      console.error("Google login error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate]);

  return {
    profile,
    isUserLoggedIn,
    isLoading,
    error,
    login,
    loginWithGoogle,
    logout,
  };
};

export default useAuth;
